import { TCreateMatchReqDto, TCreateMatchResDto } from "@/api/services/admin/match/dtos/create-match.dto";
import { TGetMatchListResDto } from "@/api/services/admin/match/dtos/get-match-list.dto";
import { TGetMatchResDto } from "@/api/services/admin/match/dtos/get-match.dto";
import { TSwitchUserAsAdminResDto } from "@/api/services/admin/match/dtos/switch-user-as-admin.dto";
import { TUpdateMatchReqDto, TUpdateMatchResDto } from "@/api/services/admin/match/dtos/update-match.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { usePagination } from "@/hooks/usePagination";
import { useIsPageParamValid } from "@/hooks/useSanitizedPageParam";
import { useIsMatchSortValid } from "@/features/match/hooks/use-match-sort-validation";
import { TApiMatchListPagReq } from "@/types/api";
import { EMatchContractStatus } from "@/types/enum";
import { clientApi } from "@/utils/api";
import { isNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { TGetMatchUserListResDto, TMatchUserListReqDto } from "@/api/services/admin/match/dtos/get-match-user-list.dto";

export const createMatchApi = async (payload: TCreateMatchReqDto): Promise<TCreateMatchResDto> => {
  const rs = await clientApi.post<TCreateMatchResDto>("/matches", payload);
  return rs.data;
};

export const fetchMatchApi = async (matchId: string): Promise<TGetMatchResDto> => {
  const rs = await clientApi.get<TGetMatchResDto>(`/matches/${matchId}`);
  return rs.data;
};

export const useMatch = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.MATCH.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchMatchApi(id) : undefined),
    enabled: Boolean(id && isNumber(id)),
  });
};

export const fetchMatchListApi = async (req: TApiMatchListPagReq): Promise<TGetMatchListResDto> => {
  const url = createUrlWithParams("/matches", req);

  const rs = await clientApi.get<TGetMatchListResDto>(url);
  return rs.data;
};

export const fetchMatchUserListApi = async (matchId?: string, req?: TMatchUserListReqDto) => {
  const url = createUrlWithParams(`matches/${matchId}/users`, req);
  const rs = await clientApi.get<TGetMatchUserListResDto>(url);
  return rs.data;
};

export const useMatchList = (contractStatuses?: EMatchContractStatus[]) => {
  const pagReq = usePagination();
  const isValidSort = useIsMatchSortValid();
  const isValidPage = useIsPageParamValid();
  const params = {
    contractStatuses,
    ...pagReq,
  };
  return useQuery({
    queryKey: [QUERY_KEYS.MATCH.FETCH_LIST, params],
    queryFn: () => fetchMatchListApi(params),
    enabled: isValidSort && isValidPage,
  });
};

export const useMatchUserList = (matchId?: string, isMatchUser?: number) => {
  const pagReq = usePagination();
  const isValidPage = useIsPageParamValid();
  return useQuery({
    queryKey: [QUERY_KEYS.MATCH.FETCH_USER_LIST, pagReq, isMatchUser],
    queryFn: () => fetchMatchUserListApi(matchId, { ...pagReq, isMatchUser }),
    enabled: Boolean(matchId) && isValidPage,
    gcTime: 0,
    staleTime: 0,
  });
};

export const setMatchAdminApi = async (matchId: string, userId: number): Promise<TSwitchUserAsAdminResDto> => {
  const rs = await clientApi.put<TSwitchUserAsAdminResDto>(`/matches/${matchId}/users/${userId}/set-as-admin`);
  return rs.data;
};

export const updateMatchApi = async (matchId: string, payload: TUpdateMatchReqDto): Promise<TUpdateMatchResDto> => {
  const rs = await clientApi.put<TUpdateMatchResDto>(`/matches/${matchId}`, payload);
  return rs.data;
};

export const deleteMatchApi = async (matchId: number | string): Promise<{ message: string }> => {
  const rs = await clientApi.delete<{ message: string }>(`/matches/${matchId}`);
  return rs.data;
};

const getContractStatusCount = async () => {
  const rs = await clientApi.get("/matches/get-contract-status-count");
  return rs.data;
};

export const useCountContractStatusCount = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.MATCH.GET_CONTRACT_STATUS_COUNT],
    queryFn: () => getContractStatusCount(),
  });
};
